import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

// Global
const whiteLabelSubject = new ReplaySubject(1);
const evergreenModalClosed = new Subject();

// Error messages
const errorMessageAddressed = new Subject();
const errorMessageMarkedCompleted = new Subject();

// Generate Captions
const generateCaptionsOpen = new Subject();
const captionsGenerated = new Subject();
const singleCaptionGenerated = new Subject();

// Emojis
const addContentEmoji = new Subject();
const broadcastContentEmoji = new Subject();
const scheduledContentEmoji = new Subject();
const bulkContentEmoji = new Subject();
const contentCommentEmoji = new Subject();

// Post Status
const postStatusUpdate$ = new Subject();

// Post Retry Errors
const postRetryErrors$ = new Subject();

// User Updates
const userUpdates$ = new Subject();

// Tag Updates
const tagUpdates$ = new Subject();

// Workspace Updates
const workspaceUpdates$ = new Subject();

// Linked Account Updates
const linkedAccountUpdates$ = new ReplaySubject(1);

// Agency Settings Updates
const settingsUpdate$ = new ReplaySubject(1);

// Hashtags
const hashtagsUpdate$ = new Subject();

// CC Context for Tellium
const updateCCContext$ = new Subject();

// Canva Images
const stockImages$ = new Subject();

// Content Tags
const contentTags$ = new Subject();

// Getting Started Guide
const gettingStartedGuideOpen$ = new ReplaySubject();

// Refresh Credentials
const refreshCredentials$ = new Subject();

// Alerts Info
const alertsUpdate$ = new ReplaySubject();

// User Info Changes
const userInfoChanged$ = new Subject();

// User Info Prompt
const userInfoPromptOpen$ = new Subject();

// Hidden Social Platforms
const hiddenSocialPlatforms$ = new Subject();

// save workspace profile
const workspaceProfileUpdate$ = new ReplaySubject(1);

// WebSocket Messages
const webSocketMessages$ = new Subject();

function subscribeToWebSocketEvent(eventName, callback) {
  return webSocketMessages$
    .pipe(filter(msg => msg.event === eventName))
    .subscribe({ next: callback });
}

// WebSocket Connection Status
const webSocketConnectionStatus$ = new ReplaySubject(1);

// Notification Settings
const notificationSettingsUpdate$ = new Subject();

// Preview Badge State (React to Angular bridge)
const previewBadge$ = new BehaviorSubject(false);

// Ad-Hoc Schedule Dialog
const adHocScheduleDialog$ = new BehaviorSubject({
  open: false,
  minimized: false,
  postTimes: [],
  timezone: '',
  wl: null,
});

// Content Creation Modal minimized state (calendar integration only)
const contentCreationModal$ = new BehaviorSubject({
  minimized: false,
  postTimes: [],
  timezone: '',
});

const adHocScheduleActions = {
  open: ({ postTimes, timezone, wl }) => {
    adHocScheduleDialog$.next({
      open: true,
      minimized: false,
      postTimes: postTimes || [],
      timezone: timezone || '',
      wl: wl || null,
    });
  },
  close: () => {
    adHocScheduleDialog$.next({
      open: false,
      minimized: false,
      postTimes: [],
      timezone: '',
      wl: null,
      action: undefined,
    });
  },
  minimize: ({ minimized }) => {
    const current = adHocScheduleDialog$.getValue();
    adHocScheduleDialog$.next({ ...current, minimized });
  },
  addPostTime: ({ postTime }) => {
    const current = adHocScheduleDialog$.getValue();
    adHocScheduleDialog$.next({
      ...current,
      postTimes: [...current.postTimes, postTime].sort(),
    });
  },
  setPostTimes: ({ postTimes }) => {
    const current = adHocScheduleDialog$.getValue();
    adHocScheduleDialog$.next({
      ...current,
      postTimes,
    });
  },
  setTimezone: ({ timezone }) => {
    const current = adHocScheduleDialog$.getValue();
    adHocScheduleDialog$.next({
      ...current,
      timezone,
    });
  },
  createPost: () => {
    const current = adHocScheduleDialog$.getValue();
    adHocScheduleDialog$.next({
      ...current,
      action: 'CREATE_POST',
      open: false,
    });
  },
  createStory: () => {
    const current = adHocScheduleDialog$.getValue();
    adHocScheduleDialog$.next({
      ...current,
      action: 'CREATE_STORY',
      open: false,
    });
  },
};

const contentCreationModalActions = {
  setMinimized: ({ minimized, postTimes, timezone }) => {
    contentCreationModal$.next({
      minimized,
      postTimes: postTimes || [],
      timezone: timezone || '',
    });
  },
  addPostTime: ({ postTime }) => {
    const current = contentCreationModal$.getValue();
    contentCreationModal$.next({
      ...current,
      postTimes: [...current.postTimes, postTime].sort(),
    });
  },
  clear: () => {
    contentCreationModal$.next({
      minimized: false,
      postTimes: [],
      timezone: '',
    });
  },
};

export {
  whiteLabelSubject,
  evergreenModalClosed,
  errorMessageAddressed,
  errorMessageMarkedCompleted,
  generateCaptionsOpen,
  captionsGenerated,
  singleCaptionGenerated,
  addContentEmoji,
  broadcastContentEmoji,
  scheduledContentEmoji,
  bulkContentEmoji,
  contentCommentEmoji,
  postStatusUpdate$,
  postRetryErrors$,
  userUpdates$,
  tagUpdates$,
  workspaceUpdates$,
  linkedAccountUpdates$,
  settingsUpdate$,
  hashtagsUpdate$,
  updateCCContext$,
  contentTags$,
  stockImages$,
  gettingStartedGuideOpen$,
  refreshCredentials$,
  alertsUpdate$,
  userInfoChanged$,
  userInfoPromptOpen$,
  hiddenSocialPlatforms$,
  workspaceProfileUpdate$,
  webSocketMessages$,
  webSocketConnectionStatus$,
  notificationSettingsUpdate$,
  previewBadge$,
  adHocScheduleDialog$,
  adHocScheduleActions,
  contentCreationModal$,
  contentCreationModalActions,
  subscribeToWebSocketEvent,
};
